import { useRef, useState, useEffect } from "react";
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import SubHeadlineSection from "./components/SubHeadlineSection";
import BulletsSection from "./components/BulletsSection";
import FAQSection from "./components/FAQSection";
import LeadFormSection from "./components/LeadFormSection";
import ExitIntentPopup from "./components/ExitIntentPopup";

export default function Home() {
  const formRef = useRef<HTMLDivElement>(null);
  const formSectionRef = useRef<HTMLDivElement>(null);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [formVisible, setFormVisible] = useState(false);

  // JSON-LD Schema
  useEffect(() => {
    const siteUrl = (import.meta.env.VITE_SITE_URL as string) || '';
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'home-schema-jsonld';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${siteUrl}/#organization`,
          name: 'Capital Growth Studio',
          url: siteUrl,
          description:
            'Med spa marketing agency specializing in helping medical spas get 10–30 new bookings in 30 days through proven growth strategies.',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Manhattan',
            addressRegion: 'NY',
            addressCountry: 'US',
          },
          founder: {
            '@type': 'Person',
            name: 'Guilherme Oliveira',
          },
        },
        {
          '@type': 'Service',
          name: 'Free Med Spa Growth Audit',
          provider: { '@id': `${siteUrl}/#organization` },
          description:
            'A free 15-minute personalized growth audit for med spas to discover exactly what is blocking their growth and how to get 10–30 new bookings in 30 days.',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
          },
        },
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/`,
          url: `${siteUrl}/`,
          name: 'Free Med Spa Growth Audit — Get 10–30 New Bookings in 30 Days',
          description:
            'Is your med spa missing 20–40 new clients every month? Get a free 15-minute audit and discover exactly what\'s blocking your growth — plus a simple action plan to get 10–30 new bookings in 30 days.',
          isPartOf: {
            '@type': 'WebSite',
            '@id': `${siteUrl}/#website`,
            url: siteUrl,
            name: 'Capital Growth Studio',
          },
        },
        {
          '@type': 'FAQPage',
          mainEntity: [],
        },
      ],
    });
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById('home-schema-jsonld');
      if (existing) existing.remove();
    };
  }, []);

  const handleCtaClick = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Show sticky CTA after scrolling 400px
  useEffect(() => {
    const onScroll = () => {
      setShowStickyCta(window.scrollY > 400);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hide sticky CTA when form section is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setFormVisible(entry.isIntersecting),
      { threshold: 0.25 }
    );
    if (formSectionRef.current) observer.observe(formSectionRef.current);
    return () => observer.disconnect();
  }, []);

  const ctaVisible = showStickyCta && !formVisible;

  return (
    <main style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Exit-intent popup */}
      <ExitIntentPopup onCtaClick={handleCtaClick} />

      {/* Header */}
      <Header onCtaClick={handleCtaClick} />

      {/* Section 1: Headline */}
      <HeroSection onCtaClick={handleCtaClick} />

      {/* Section 2: Sub-headline */}
      <SubHeadlineSection />

      {/* Section 3: Bullet Points */}
      <BulletsSection />

      {/* Section 4: FAQ */}
      <FAQSection />

      {/* Section 5: Lead Form */}
      <div ref={formSectionRef}>
        <LeadFormSection ref={formRef} />
      </div>

      {/* Footer */}
      <footer
        className="py-8 px-6 text-center"
        style={{ background: "#F5F2EE", borderTop: "1px solid #E8E5E0" }}
      >
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img
            src="https://static.readdy.ai/image/b1af610ebea9d5dff7bba4d7db421a2d/efa59382ce49994f108d3b6dc4f24e13.png"
            alt="Capital Growth Studio"
            className="h-10 object-contain"
          />
          <p
            className="text-xs"
            style={{ color: "#A0A0A0", fontFamily: "'Inter', sans-serif" }}
          >
            &copy; {new Date().getFullYear()} All rights reserved. &nbsp;|&nbsp; This site is
            protected by my privacy policy.
          </p>
        </div>
      </footer>

      {/* Sticky CTA Button */}
      <div
        className="fixed bottom-6 left-1/2 z-50"
        style={{
          transform: "translateX(-50%)",
          opacity: ctaVisible ? 1 : 0,
          pointerEvents: ctaVisible ? "auto" : "none",
          transition: "opacity 0.4s ease, transform 0.4s ease",
          willChange: "opacity, transform",
        }}
      >
        <button
          onClick={handleCtaClick}
          className="whitespace-nowrap flex items-center gap-2.5 px-7 py-4 rounded-full font-semibold text-white text-sm cursor-pointer transition-all duration-300 hover:scale-105 active:scale-95"
          style={{
            background: "linear-gradient(135deg, #C9956F 0%, #B07048 100%)",
            fontFamily: "'Inter', sans-serif",
            boxShadow: "0 8px 32px rgba(176, 112, 72, 0.45)",
          }}
        >
          <i className="ri-calendar-check-line text-base" />
          Get My Free 15-Minute Audit
          <i className="ri-arrow-right-line text-base" />
        </button>
      </div>
    </main>
  );
}
